import React, { useEffect, useState } from 'react';
import { Sparkles, Leaf, TrendingUp, Globe2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, pickCmsShort, pickCmsText, makeSectionCopyPicker } from '../services/publicContentService';

// Two official Vision 2030 logo variants — light-mode (dark text on transparent) vs dark-mode (light text on transparent)
const DEFAULT_LOGO_LIGHT =
  'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/jqojfw9o_vision-2030-logo.png';
const DEFAULT_LOGO_DARK =
  'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/y53vsaed_vision2030-saudi-arabia-Logo-PNG-Transparent-Background.png';

// Named icon pool — admin only supplies the icon name string, we look it up here.
const ICON_MAP = { Leaf, TrendingUp, Globe2, Sparkles };

const Vision2030 = ({ theme = 'light' }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const isDark = theme === 'dark';

  const defaultPillars = [
    {
      icon: Leaf,
      title: isAr ? 'مجتمع حيوي' : 'Vibrant Society',
      contribution: isAr ? 'حماية البيئة وجودة الحياة' : 'Environment & Quality of Life',
      desc: isAr
        ? 'نحافظ على الموارد الطبيعية ونحمي التنوع البيولوجي للأجيال القادمة.'
        : 'We safeguard natural resources and protect biodiversity for future generations.',
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    },
    {
      icon: TrendingUp,
      title: isAr ? 'اقتصاد مزدهر' : 'Thriving Economy',
      contribution: isAr ? 'الاقتصاد الأخضر والاستدامة' : 'Green Economy & Sustainability',
      desc: isAr
        ? 'نُمكّن المنشآت من تبني حلول بيئية تعزز التنافسية وتدعم النمو المستدام.'
        : 'We enable organisations to adopt sustainable solutions that drive growth.',
      gradient: 'from-amber-400 via-orange-400 to-amber-500',
    },
    {
      icon: Globe2,
      title: isAr ? 'وطن طموح' : 'Ambitious Nation',
      contribution: isAr ? 'الحوكمة البيئية والامتثال' : 'Environmental Governance',
      desc: isAr
        ? 'نُشارك في رسم سياسات بيئية فعّالة تواكب الطموح الوطني نحو 2030.'
        : 'We help shape effective environmental policy aligned with national ambition.',
      gradient: 'from-cyan-400 via-blue-400 to-sky-500',
    },
  ];

  // CMS-driven pillars — expects settings.vision2030_pillars.items to be a
  // 3-item list. Missing / malformed items fall back to defaults.
  const [pillars, setPillars] = useState(defaultPillars);
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('vision2030_pillars').then((v) => {
      if (!alive) return;
      const list = Array.isArray(v?.items) ? v.items : null;
      if (!list || list.length === 0) return;
      const mapped = list.slice(0, 3).map((raw, i) => ({
        icon: ICON_MAP[raw.icon] || defaultPillars[i].icon,
        title:        (isAr ? raw.title_ar        : raw.title_en)        || defaultPillars[i].title,
        contribution: (isAr ? raw.contribution_ar : raw.contribution_en) || defaultPillars[i].contribution,
        desc:         (isAr ? raw.description_ar  : raw.description_en)  || defaultPillars[i].desc,
        gradient:     raw.gradient || defaultPillars[i].gradient,
      }));
      // Only replace when the CMS supplied at least three complete entries.
      if (mapped.length === 3) setPillars(mapped);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAr]);

  // Vision2030 hero (Phase B)
  const [hero, setHero] = useState({});
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('vision2030_hero').then((v) => { if (alive) setHero(v || {}); });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);

  const logoLight = (hero.logo_light && String(hero.logo_light).trim()) || DEFAULT_LOGO_LIGHT;
  const logoDark  = (hero.logo_dark  && String(hero.logo_dark).trim())  || DEFAULT_LOGO_DARK;
  const pledgeTitle = pickCmsShort(hero.pledge_title, isAr) || (isAr
    ? 'نُترجم طموح المملكة إلى ممارسات بيئية ملموسة'
    : 'Turning national ambition into measurable environmental impact');
  const pledgeBody = pickCmsText(hero.pledge_body, isAr, 60) || (isAr
    ? 'تلتزم شركة التوازن البيئي بدعم رؤية المملكة 2030 من خلال استشارات بيئية متخصصة تُسرّع من التحول نحو الاستدامة، وتحمي البيئة للأجيال القادمة، وتُمكّن القطاعات الحيوية من تحقيق أهدافها التنموية بمسؤولية.'
    : 'EBC is committed to supporting Saudi Vision 2030 through specialised environmental consultancy that accelerates the shift toward sustainability, protects the environment for future generations, and empowers vital sectors to meet their goals responsibly.');

  return (
    <section
      id="vision-2030"
      dir={isAr ? 'rtl' : 'ltr'}
      className="relative py-32 overflow-hidden transition-colors duration-700"
    >
      {/* Theme-aware base — light: soft sky/sand gradient; dark: deep ocean */}
      {isDark ? (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-50 via-blue-50 to-emerald-50" />
      )}

      {/* Soft radial spotlight near logo — colored differently per mode */}
      <div
        className="absolute top-1/3 -translate-y-1/2 w-[900px] h-[900px] pointer-events-none"
        style={{
          [isAr ? 'right' : 'left']: '-200px',
          background: isDark
            ? 'radial-gradient(closest-side, rgba(34,211,238,0.18), rgba(34,211,238,0.06) 40%, transparent 70%)'
            : 'radial-gradient(closest-side, rgba(20,184,166,0.18), rgba(34,211,238,0.08) 40%, transparent 70%)',
        }}
      />

      {/* Vertical light rays */}
      <div className={`absolute inset-0 pointer-events-none ${isDark ? 'opacity-30' : 'opacity-40'}`}>
        <div
          className={`absolute top-0 left-[15%] w-px h-full bg-gradient-to-b ${
            isDark ? 'from-cyan-400/40' : 'from-cyan-500/30'
          } to-transparent`}
        />
        <div
          className={`absolute top-0 left-[45%] w-px h-full bg-gradient-to-b ${
            isDark ? 'from-blue-400/30' : 'from-emerald-500/25'
          } to-transparent`}
        />
        <div
          className={`absolute top-0 left-[75%] w-px h-full bg-gradient-to-b ${
            isDark ? 'from-emerald-400/20' : 'from-blue-500/20'
          } to-transparent`}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full ${isDark ? 'bg-cyan-300/40' : 'bg-cyan-500/30'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative container mx-auto px-4">
        {/* Eyebrow + Section Title */}
        <div className="text-center mb-20 animate-fade-in-up">
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 ${
              isDark
                ? 'bg-cyan-500/10 border border-cyan-500/30'
                : 'bg-cyan-600/10 border border-cyan-600/30'
            }`}
          >
            <Sparkles className={`w-4 h-4 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`} />
            <span
              className={`text-xs tracking-[0.3em] uppercase ${
                isDark ? 'text-cyan-200' : 'text-cyan-800'
              }`}
              data-testid="vision2030-eyebrow"
            >
              {sec('vision2030_eyebrow', isAr ? 'شراكة وطنية' : 'National Alignment')}
            </span>
          </div>
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 tracking-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
            data-testid="vision2030-heading"
          >
            {sec('vision2030_heading_prefix', isAr ? 'ملتزمون بـ ' : 'Aligned with ')}
            <span
              className={`bg-clip-text text-transparent ${
                isDark
                  ? 'bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-300'
                  : 'bg-gradient-to-r from-cyan-700 via-emerald-600 to-amber-600'
              }`}
            >
              {sec('vision2030_heading_highlight', isAr ? 'رؤية 2030' : 'Vision 2030')}
            </span>
          </h2>
          <p
            className={`text-base md:text-lg max-w-2xl mx-auto leading-relaxed ${
              isDark ? 'text-cyan-100/70' : 'text-slate-600'
            }`}
            data-testid="vision2030-subtitle"
          >
            {sec('vision2030_subtitle', isAr
              ? 'نُسخّر خبراتنا البيئية لخدمة الركائز الثلاث للرؤية الوطنية، مساهمين في صنع غد أكثر استدامة للمملكة.'
              : 'We channel our environmental expertise to serve the three pillars of the national vision — building a more sustainable tomorrow for the Kingdom.')}
          </p>
        </div>

        {/* Hero Split: Logo Spotlight + Statement */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-24 max-w-7xl mx-auto">
          {/* Logo spotlight */}
          <div className="lg:col-span-5 relative group order-2 lg:order-1">
            <div className="relative aspect-square max-w-md mx-auto">
              {/* Concentric rings */}
              <div
                className={`absolute inset-0 rounded-full border animate-pulse-slow ${
                  isDark ? 'border-cyan-400/20' : 'border-cyan-600/25'
                }`}
              />
              <div
                className={`absolute inset-6 rounded-full border ${
                  isDark ? 'border-cyan-400/15' : 'border-cyan-600/20'
                }`}
                style={{ animation: 'pulse 4s ease-in-out infinite', animationDelay: '0.5s' }}
              />
              <div
                className={`absolute inset-12 rounded-full border ${
                  isDark ? 'border-cyan-400/10' : 'border-cyan-600/15'
                }`}
                style={{ animation: 'pulse 4s ease-in-out infinite', animationDelay: '1s' }}
              />

              {/* Halo */}
              <div
                className="absolute inset-8 rounded-full blur-3xl opacity-60"
                style={{
                  background: isDark
                    ? 'radial-gradient(circle, rgba(34,211,238,0.4), rgba(16,185,129,0.2) 40%, transparent 70%)'
                    : 'radial-gradient(circle, rgba(20,184,166,0.35), rgba(34,211,238,0.18) 40%, transparent 70%)',
                }}
              />

              {/* Logo — swap based on theme */}
              <div className="relative h-full w-full flex items-center justify-center p-12 transition-transform duration-700 group-hover:scale-105">
                <img
                  key={isDark ? 'dark' : 'light'}
                  src={isDark ? logoDark : logoLight}
                  alt="Vision 2030 Saudi Arabia"
                  className="w-full h-full object-contain transition-opacity duration-500"
                  style={{
                    filter: isDark
                      ? 'drop-shadow(0 0 40px rgba(34,211,238,0.45)) drop-shadow(0 0 80px rgba(16,185,129,0.25))'
                      : 'drop-shadow(0 8px 20px rgba(15,23,42,0.15)) drop-shadow(0 0 60px rgba(20,184,166,0.2))',
                  }}
                />
              </div>

              {/* Corner accents */}
              <div
                className={`absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 rounded-tr-3xl ${
                  isDark ? 'border-cyan-400/40' : 'border-cyan-600/50'
                }`}
              />
              <div
                className={`absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 rounded-bl-3xl ${
                  isDark ? 'border-cyan-400/40' : 'border-cyan-600/50'
                }`}
              />
            </div>
          </div>

          {/* Statement */}
          <div className={`lg:col-span-7 order-1 lg:order-2 ${isAr ? 'text-right' : 'text-left'}`}>
            <div className="space-y-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className={`inline-flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
                <div
                  className={`h-px w-12 ${
                    isDark
                      ? 'bg-gradient-to-r from-cyan-400 to-transparent'
                      : 'bg-gradient-to-r from-cyan-700 to-transparent'
                  }`}
                />
                <span
                  className={`text-sm font-semibold tracking-wider ${
                    isDark ? 'text-cyan-300' : 'text-cyan-800'
                  }`}
                >
                  {sec('vision2030_pledge_label', isAr ? 'رسالتنا' : 'Our Pledge')}
                </span>
              </div>

              <h3
                className={`text-3xl md:text-4xl lg:text-5xl font-bold leading-tight ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
                data-testid="vision2030-pledge-title"
              >
                {pledgeTitle}
              </h3>

              <p
                className={`text-lg leading-loose ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
                data-testid="vision2030-pledge-body"
              >
                {pledgeBody}
              </p>

              {/* Quick-stats strip */}
              <div className={`flex flex-wrap gap-6 pt-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                <div>
                  <div
                    className={`text-3xl font-bold ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}
                  >
                    3
                  </div>
                  <div
                    className={`text-xs tracking-wider mt-1 uppercase ${
                      isDark ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {sec('vision2030_stat_pillars', isAr ? 'ركائز رئيسية' : 'Core Pillars')}
                  </div>
                </div>
                <div className={isDark ? 'w-px bg-white/10' : 'w-px bg-slate-300'} />
                <div>
                  <div
                    className={`text-3xl font-bold ${
                      isDark ? 'text-emerald-300' : 'text-emerald-700'
                    }`}
                  >
                    2030
                  </div>
                  <div
                    className={`text-xs tracking-wider mt-1 uppercase ${
                      isDark ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {sec('vision2030_stat_horizon', isAr ? 'الأفق الوطني' : 'National Horizon')}
                  </div>
                </div>
                <div className={isDark ? 'w-px bg-white/10' : 'w-px bg-slate-300'} />
                <div>
                  <div
                    className={`text-3xl font-bold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}
                  >
                    100%
                  </div>
                  <div
                    className={`text-xs tracking-wider mt-1 uppercase ${
                      isDark ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {sec('vision2030_stat_commitment', isAr ? 'التزام' : 'Commitment')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Three Pillars */}
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <h3
              className={`text-2xl md:text-3xl font-bold mb-3 ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              {sec('vision2030_contribution_heading', isAr ? 'مساهمتنا في الركائز الثلاث' : 'Our Contribution to the Three Pillars')}
            </h3>
            <div
              className={`w-20 h-0.5 mx-auto bg-gradient-to-r from-transparent ${
                isDark ? 'via-cyan-400' : 'via-cyan-600'
              } to-transparent`}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group relative animate-fade-in-up"
                  style={{ animationDelay: `${0.4 + i * 0.15}s` }}
                  data-testid={`vision-pillar-${i}`}
                >
                  <div
                    className={`relative h-full p-8 rounded-2xl backdrop-blur-xl
                                transition-all duration-500
                                hover:-translate-y-1 overflow-hidden ${
                                  isDark
                                    ? 'bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                                    : 'bg-white/70 border border-slate-200 hover:bg-white hover:border-slate-300 shadow-md hover:shadow-xl'
                                }`}
                  >
                    {/* Top gradient bar */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${p.gradient} opacity-60`}
                    />

                    {/* Hover glow */}
                    <div
                      className={`absolute -inset-px rounded-2xl bg-gradient-to-br ${p.gradient}
                                  opacity-0 group-hover:opacity-10 blur-2xl transition-opacity duration-500`}
                    />

                    <div className="relative">
                      <div
                        className={`w-14 h-14 rounded-xl bg-gradient-to-br ${p.gradient}
                                    flex items-center justify-center mb-6 shadow-lg
                                    transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}
                      >
                        <Icon className="w-7 h-7 text-white" />
                      </div>

                      <div
                        className={`text-xs tracking-[0.3em] uppercase mb-2 ${
                          isDark ? 'text-cyan-300/70' : 'text-cyan-700/80'
                        }`}
                      >
                        {isAr
                          ? `الركيزة ${['الأولى', 'الثانية', 'الثالثة'][i]}`
                          : `Pillar ${String(i + 1).padStart(2, '0')}`}
                      </div>

                      <h4
                        className={`text-2xl font-bold mb-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}
                      >
                        {p.title}
                      </h4>

                      <div
                        className={`inline-block text-sm font-medium mb-4 bg-gradient-to-r ${p.gradient} bg-clip-text text-transparent`}
                      >
                        {p.contribution}
                      </div>

                      <p
                        className={`leading-relaxed text-[15px] ${
                          isDark ? 'text-slate-300/90' : 'text-slate-600'
                        }`}
                      >
                        {p.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Vision2030;
